import {
  functionalUpdate,
  useTable,
  type PaginationState,
  type ReactTable,
  type RowData,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import type { SortDescriptor } from '@/api/paged-query';
import { createLocalStore, useLocalStore, type LocalStore } from '@/lib/local-store';
import {
  activeFilterKeys,
  formatSortParam,
  normalizeSearch,
  pageSizeStore,
  parseSortParam,
  type ListSearch,
} from '@/lib/url-state';
import { dataTableFeatures, type DataTableColumnDef, type DataTableFeatures } from './columns';

export type Density = 'comfortable' | 'compact';

export interface TableView {
  columnVisibility: Record<string, boolean>;
  density: Density;
}

const viewSchema = z.object({
  columnVisibility: z.record(z.string(), z.boolean()),
  density: z.enum(['comfortable', 'compact']),
});

const DEFAULT_VIEW: TableView = { columnVisibility: {}, density: 'comfortable' };
const viewStores = new Map<string, LocalStore<TableView>>();

/** Per-table column visibility and density, kept in this browser (`rgs.table.<id>.view`). */
export function tableViewStore(id: string): LocalStore<TableView> {
  let store = viewStores.get(id);
  if (!store) {
    store = createLocalStore(`rgs.table.${id}.view`, viewSchema, DEFAULT_VIEW);
    viewStores.set(id, store);
  }
  return store;
}

/** TanStack's own id rule: explicit id, else the accessor key with dots as underscores. */
export function columnId<TData extends RowData>(def: DataTableColumnDef<TData>): string | undefined {
  if (def.id) return def.id;
  const key = 'accessorKey' in def ? def.accessorKey : undefined;
  if (key !== undefined) return String(key).replaceAll('.', '_');
  return typeof def.header === 'string' ? def.header : undefined;
}

export interface UseDataTableOptions<TData extends RowData, TSearch extends ListSearch> {
  /** Stable id for the stored view and saved views, e.g. `currencies`. */
  id: string;
  columns: DataTableColumnDef<TData>[];
  /** The current page from the server; undefined while the first page loads. */
  data: readonly TData[] | undefined;
  total: number | undefined;
  getRowId: (row: TData) => string;
  /** The route's validated search params, and how to change them (usually `navigate({search})`). */
  search: TSearch;
  onSearchChange: (next: TSearch, options?: { replace?: boolean }) => void;
  /** What the server sorts by when the URL has no sort; shown as the active sort. */
  defaultSort?: SortDescriptor;
  enableSelection?: boolean | ((row: TData) => boolean);
}

export interface DataTableController<TData extends RowData, TSearch extends ListSearch> {
  table: ReactTable<DataTableFeatures, TData>;
  id: string;
  total: number | undefined;
  search: TSearch;
  /** Merge into the search params. Anything but a page change goes back to page 1 and clears the selection. */
  setSearch: (patch: Partial<TSearch>, options?: { replace?: boolean }) => void;
  /** Replace the search params wholesale (saved views). */
  replaceSearch: (next: TSearch) => void;
  activeFilters: string[];
  clearFilters: () => void;
  density: Density;
  setDensity: (density: Density) => void;
  columnVisibility: Record<string, boolean>;
  resetView: () => void;
  applyView: (view: TableView) => void;
  /** Selected rows, including ones selected on other pages. */
  selectedRows: TData[];
  clearSelection: () => void;
}

const EMPTY: never[] = [];

interface Selection<TData> {
  ids: RowSelectionState;
  rows: Map<string, TData>;
}

export function useDataTable<TData extends RowData, TSearch extends ListSearch>(
  options: UseDataTableOptions<TData, TSearch>,
): DataTableController<TData, TSearch> {
  const { id, columns, data, total, getRowId, search, onSearchChange, defaultSort, enableSelection = false } = options;
  const rows = data ?? EMPTY;

  const store = tableViewStore(id);
  const [storedView, setStoredView] = useLocalStore(store);
  const defaultVisibility = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const def of columns) {
      const cid = columnId(def);
      if (cid && def.meta?.hiddenByDefault) out[cid] = false;
    }
    return out;
  }, [columns]);
  const columnVisibility = useMemo(
    () => ({ ...defaultVisibility, ...storedView.columnVisibility }),
    [defaultVisibility, storedView.columnVisibility],
  );

  const [selection, setSelection] = useState<Selection<TData>>({ ids: {}, rows: new Map() });
  const clearSelection = useCallback(() => setSelection({ ids: {}, rows: new Map() }), []);

  const setSearch = useCallback(
    (patch: Partial<TSearch>, opts?: { replace?: boolean }) => {
      const onlyPaging = Object.keys(patch).every((k) => k === 'page');
      if (!Object.keys(patch).every((k) => k === 'page' || k === 'size')) clearSelection();
      const next = { ...search, ...(onlyPaging ? {} : { page: undefined }), ...patch };
      onSearchChange(normalizeSearch(next), opts);
    },
    [search, onSearchChange, clearSelection],
  );

  const replaceSearch = useCallback(
    (next: TSearch) => {
      clearSelection();
      onSearchChange(normalizeSearch(next));
    },
    [onSearchChange, clearSelection],
  );

  const [storedPageSize] = useLocalStore(pageSizeStore);
  const pageSize = search.size ?? storedPageSize;
  // The size a URL names (a pick here, or a link) is the one every list opens on next. Synced from
  // the URL rather than set on the pick, so the page reset and the new size land together.
  useEffect(() => {
    if (search.size !== undefined && search.size !== pageSizeStore.get()) pageSizeStore.set(search.size);
  }, [search.size]);
  const pageIndex = Math.max(0, (search.page ?? 1) - 1);
  const pagination = useMemo<PaginationState>(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const sortFieldToColumn = useMemo(() => {
    const map = new Map<string, string>();
    for (const def of columns) {
      const cid = columnId(def);
      if (cid && def.meta?.sortField) map.set(def.meta.sortField, cid);
    }
    return map;
  }, [columns]);
  const columnToSortField = useMemo(
    () => new Map([...sortFieldToColumn].map(([field, cid]) => [cid, field])),
    [sortFieldToColumn],
  );

  const activeSort = parseSortParam(search.sort) ?? defaultSort ?? null;
  const activeSortColumn = activeSort ? sortFieldToColumn.get(activeSort.field) : undefined;
  const activeSortDesc = activeSort?.dir === 'desc';
  const sorting = useMemo<SortingState>(
    () => (activeSortColumn ? [{ id: activeSortColumn, desc: activeSortDesc }] : []),
    [activeSortColumn, activeSortDesc],
  );

  const table = useTable({
    features: dataTableFeatures,
    columns,
    data: rows,
    getRowId: (row) => getRowId(row),
    rowCount: total ?? 0,
    manualPagination: true,
    manualSorting: true,
    enableMultiSort: false,
    enableRowSelection:
      typeof enableSelection === 'function' ? (row) => enableSelection(row.original) : enableSelection,
    state: { pagination, sorting, columnVisibility, rowSelection: selection.ids },
    onPaginationChange: (updater) => {
      const next = functionalUpdate(updater, pagination);
      if (next.pageSize !== pageSize) setSearch({ size: next.pageSize } as Partial<TSearch>);
      else if (next.pageIndex !== pageIndex) setSearch({ page: next.pageIndex + 1 } as Partial<TSearch>);
    },
    onSortingChange: (updater) => {
      const [first] = functionalUpdate(updater, sorting);
      const field = first ? columnToSortField.get(first.id) : undefined;
      const next = field ? formatSortParam({ field, dir: first?.desc ? 'desc' : 'asc' }) : undefined;
      // Sorting back to the default is the same view as no sort: keep the URL clean.
      const isDefault = next === formatSortParam(defaultSort);
      setSearch({ sort: isDefault ? undefined : next } as Partial<TSearch>);
    },
    onColumnVisibilityChange: (updater) => {
      const next = functionalUpdate(updater, columnVisibility);
      setStoredView((prev) => ({ ...prev, columnVisibility: next }));
    },
    onRowSelectionChange: (updater) => {
      setSelection((prev) => {
        const ids = functionalUpdate(updater, prev.ids);
        const byId = new Map(rows.map((row) => [getRowId(row), row] as const));
        const selected = new Map<string, TData>();
        for (const [rowId, on] of Object.entries(ids)) {
          const row = on ? (prev.rows.get(rowId) ?? byId.get(rowId)) : undefined;
          if (row !== undefined) selected.set(rowId, row);
        }
        return { ids, rows: selected };
      });
    },
  });

  // A shared link or a shrinking result set can point past the last page: go to the last one.
  const pageCount = total === undefined ? undefined : Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (pageCount !== undefined && pageIndex + 1 > pageCount) {
      onSearchChange(normalizeSearch({ ...search, page: pageCount }), { replace: true });
    }
  }, [pageCount, pageIndex, search, onSearchChange]);

  const activeFilters = useMemo(() => activeFilterKeys(search), [search]);
  const clearFilters = useCallback(() => {
    const patch = Object.fromEntries(activeFilters.map((k) => [k, undefined])) as Partial<TSearch>;
    setSearch(patch);
  }, [activeFilters, setSearch]);

  const selectedRows = useMemo(() => [...selection.rows.values()], [selection.rows]);

  return {
    table,
    id,
    total,
    search,
    setSearch,
    replaceSearch,
    activeFilters,
    clearFilters,
    density: storedView.density,
    setDensity: (density) => setStoredView((prev) => ({ ...prev, density })),
    columnVisibility,
    resetView: () => store.reset(),
    applyView: (view) => setStoredView(view),
    selectedRows,
    clearSelection,
  };
}
